/**
 * Centralized JSON-LD schema configuration per route.
 *
 * Each route maps to an array of schema objects that will be rendered
 * as <script type="application/ld+json"> tags by the <PageSchemas> component.
 *
 * FAQ schemas that are co-located with their UI components (e.g. in service pages)
 * remain in those components — only non-FAQ schemas are centralized here.
 */

import {
  serviceSchemas,
  getServiceSchema,
  getBreadcrumbSchema,
  getWebPageSchema,
  getCollectionPageSchema,
  getItemListSchema,
  getHowToSchema,
  getJobPostingSchema,
  getArticleSchema,
  localBusinessSchema,
  singaporeLocalBusinessSchema,
} from "./schema";
import { SEO_FREE_WEBSITE, SEO_PLANS, rm, seoAnnualPrice, SEO_PLANS_SGD_MONTHLY, sgd } from "./seo-pricing";
import { brandHref, enterpriseBrands } from "@/content/enterprise/brands";
import { getBudgetPodcastAudioUrl, podcastLanguages } from "./budget-podcast";
import { founders, peoplePageCopy } from "@/content/our-people";
import { peopleStudioCopy } from "@/content/our-people-studio";
import { agkCitation, agkDocHref, agkDocs, agkPreviewDocs } from "@/content/ai-governance/kit";
import { careerRolePath, careerRoles, getCareerRoleJobDescriptionHtml } from "@/content/careers";

// Type for a schema config entry
export interface SchemaConfig {
  schemas: object[];
}

export const schemaConfigs: Record<string, SchemaConfig> = {
  "enterprise/rtx-spark-laptops-malaysia": {
    schemas: [
      getWebPageSchema({
        name: "RTX Spark Laptop Malaysia: Prices & Specs Compared",
        url: "/enterprise/rtx-spark-laptops-malaysia/",
        description: "Side-by-side comparison of 14 RTX Spark laptop configurations from ASUS, Dell, HP, Lenovo, Microsoft and MSI: manufacturer prices in RM, SGD or USD, memory, SSD, display, weight, battery and release dates, plus independent reviews. Request a preorder quote; availability, final price and delivery require confirmation.",
        dateModified: "2026-10-24",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Enterprise", url: "/enterprise/" },
        { name: "RTX Spark laptops", url: "/enterprise/rtx-spark-laptops-malaysia/" },
      ]),
    ],
  },
  // =========================================================================
  // HOMEPAGE
  // =========================================================================
  "": {
    schemas: [
      getWebPageSchema({
        name: "Leading Software Development Agency in Malaysia | Anchor Sprint",
        url: "/",
        description:
          "Anchor Sprint delivers enterprise-grade software development, AI solutions, cloud architecture, and digital transformation for Malaysian businesses.",
      }),
      getItemListSchema({
        name: "Featured Services",
        description:
          "Technology services by Anchor Sprint for Malaysian enterprises",
        items: [
          {
            name: "Software Development and Support",
            url: "/services/software-development/",
          },
          {
            name: "Cloud Architecture Design and Implementation",
            url: "/services/cloud-architecture/",
          },
          { name: "AI Solutions", url: "/services/ai-solutions/" },
        ],
      }),
      getHowToSchema({
        name: "How to Get Started with Anchor Sprint",
        description:
          "Our streamlined consultation process to begin your digital transformation project",
        steps: [
          {
            name: "Confirm your working session",
            text: "We align calendars with your core stakeholders and share a focused agenda within a few hours.",
          },
          {
            name: "Send a discovery kit",
            text: "Our consultants compile a checklist covering current systems, compliance constraints, and success metrics.",
          },
          {
            name: "Co-create the acceleration roadmap",
            text: "During the call we prioritise automation wins, outline delivery squads, and agree on executive next steps.",
          },
        ],
      }),
    ],
  },

  // =========================================================================
  // SERVICE LISTING
  // =========================================================================
  service: {
    schemas: [
      getCollectionPageSchema({
        name: "Our Services | Anchor Sprint",
        url: "/service/",
        description:
          "Comprehensive technology services — software development, AI solutions, cloud architecture, system integration, and digital transformation for Malaysian businesses.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Services", url: "/service/" },
      ]),
      getItemListSchema({
        name: "Anchor Sprint IT Services",
        description:
          "Comprehensive technology solutions for digital transformation in Malaysia",
        items: [
          {
            name: "Software Development and Support",
            url: "/services/software-development/",
          },
          {
            name: "System Integration and Support",
            url: "/services/system-integration/",
          },
          {
            name: "Cloud Architecture Design and Implementation",
            url: "/services/cloud-architecture/",
          },
          {
            name: "Website Development & Hosting",
            url: "/services/website-development/",
          },
          {
            name: "Hardware & Software Provisioning",
            url: "/services/hardware-software/",
          },
          { name: "AI Solutions", url: "/services/ai-solutions/" },
          {
            name: "Training & Education",
            url: "/services/training-education/",
          },
        ],
      }),
    ],
  },

  // =========================================================================
  // SERVICE PAGES (FAQ schemas remain in their components)
  // =========================================================================
  "services/enterprise-resilience": {
    schemas: [
      getWebPageSchema({
        name: "Enterprise Resilience Assessment: BNM RMiT & MAS",
        url: "/services/enterprise-resilience/",
        description:
          "Hands-on resilience engineering — chaos, load-to-failure, DR and SLOs — mapped to BNM RMiT & MAS, plus independent audits of existing load testing.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Services", url: "/service/" },
        { name: "Enterprise Resilience Assessment", url: "/services/enterprise-resilience/" },
      ]),
    ],
  },
  "ai-governance": {
    schemas: [
      getWebPageSchema({
        name: "AI Governance in Malaysia & Singapore",
        url: "/ai-governance/",
        description:
          "AI governance for Malaysian and Singapore companies: 2026 rules (PDPA, draft AI Bill, MAS), a 90-day plan, a free AI policy pack and an AI risk management platform.",
        dateModified: "2026-10-31",
      }),
      getArticleSchema({
        title: "AI Governance in Malaysia & Singapore",
        description:
          "What AI governance is, which rules apply in Malaysia and Singapore as at October 2026, Lite/Standard/Regulated tiers, a 90-day plan and the frameworks behind it.",
        url: "/ai-governance/",
        datePublished: "2026-10-31",
        image: "/images/ai-governance/og.jpg",
        tags: ["AI governance Malaysia", "AI governance Singapore", "AI governance framework", "PDPA", "ISO/IEC 42001"],
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "AI Governance", url: "/ai-governance/" },
      ]),
    ],
  },
  "ai-governance/ai-acceptable-use-policy-template": {
    schemas: [
      getArticleSchema({
        title: "AI Acceptable Use Policy Template",
        description:
          "What an AI acceptable use policy should cover, an excerpt of AGK-POL-02 v1.1, and the data label × AI tool tier matrix from AGK-STD-A02 v1.0.",
        url: "/ai-governance/ai-acceptable-use-policy-template/",
        datePublished: "2026-10-31",
        image: "/images/ai-governance/og.jpg",
        tags: ["AI acceptable use policy template", "AI usage policy for employees", "generative AI policy"],
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "AI Governance", url: "/ai-governance/" },
        { name: "AI Acceptable Use Policy Template", url: "/ai-governance/ai-acceptable-use-policy-template/" },
      ]),
    ],
  },
  "ai-governance/templates": {
    schemas: [
      getCollectionPageSchema({
        name: "AI Governance Templates",
        url: "/ai-governance/templates/",
        description: "Free AI governance templates with in-browser previews.",
      }),
      getItemListSchema({
        name: "AI Governance Kit templates",
        items: agkDocs.map((doc) => ({ name: `${doc.name} (${agkCitation(doc)})`, url: agkDocHref(doc) })),
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "AI Governance", url: "/ai-governance/" },
        { name: "Templates", url: "/ai-governance/templates/" },
      ]),
    ],
  },
  "compliance": {
    schemas: [
      getCollectionPageSchema({
        name: "Technology Compliance Guides for Malaysia & Singapore",
        url: "/compliance/",
        description:
          "Plain-English guides to what Malaysian and Singapore regulators and auditors expect from your technology, with paragraph references.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Compliance", url: "/compliance/" },
      ]),
      // Live guides only — keep in step with src/content/compliance/hub.ts.
      getItemListSchema({
        name: "Compliance guides",
        description: "Published Anchor Sprint compliance guides.",
        items: [
          { name: "AI governance in Malaysia and Singapore", url: "/ai-governance/" },
          { name: "What is BNM RMiT? A plain-English guide", url: "/compliance/bnm-rmit/" },
          {
            name: "RMiT Source Code Review: What BNM Requires, How to Prove It",
            url: "/blog/rmit-source-code-review-malaysia/",
          },
        ],
      }),
    ],
  },
  "compliance/bnm-rmit": {
    schemas: [
      getWebPageSchema({
        name: "What is BNM RMiT? A Plain-English Guide for Malaysian Companies",
        url: "/compliance/bnm-rmit/",
        description:
          "Bank Negara Malaysia's Risk Management in Technology (RMiT) policy in plain English: who it covers, the main requirements, the Sep 2026 reissue and what auditors check.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Compliance", url: "/compliance/" },
        { name: "BNM RMiT guide", url: "/compliance/bnm-rmit/" },
      ]),
    ],
  },
  "services/ai-solutions": {
    schemas: [
      serviceSchemas["as"],
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Services", url: "/service/" },
        { name: "AI Solutions", url: "/services/ai-solutions/" },
      ]),
    ],
  },
  "services/claude-managed-service-malaysia": {
    schemas: [
      getServiceSchema({
        name: "Claude Managed Service Malaysia",
        description:
          "Claude onboarding and managed operations for Malaysian organisations. We set up access and governance, connect approved workflows, onboard teams, monitor adoption, and provide ongoing optimisation and support.",
        url: "/services/claude-managed-service-malaysia/",
        serviceType: "Claude Managed Service",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Services", url: "/service/" },
        {
          name: "Claude Managed Service",
          url: "/services/claude-managed-service-malaysia/",
        },
      ]),
    ],
  },
  "services/ai-agents": {
    schemas: [
      getServiceSchema({
        name: "AI Agent Development Malaysia",
        description:
          "Custom AI agent development in Malaysia — agentic AI built on Claude (Claude Agent SDK and MCP) that runs real multi-step workflows: invoice processing, reporting, document matching and operations. Bespoke agent development, managed platforms, and integration with your existing tools.",
        url: "/services/ai-agents/",
        serviceType: "AI Agent Development & Automation",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Services", url: "/service/" },
        { name: "AI Agent Development", url: "/services/ai-agents/" },
      ]),
    ],
  },
  "services/ai-consulting-malaysia": {
    schemas: [
      getServiceSchema({
        name: "AI Consulting Malaysia — End-to-End AI Adoption",
        description:
          "End-to-end AI consulting across four stages: AI awareness (finding the process worth changing), AI tooling (governed tools and role-specific training), workflow redesign (rebuilding the process and building the automation into it), and adoption with measurement. Anchor Sprint supplies all four parts of an AI programme: assessment, HRD Corp claimable training, tools and platform, and custom integration and AI applications. HRD Corp approved provider; Certified in the Claude Partner Network.",
        url: "/services/ai-consulting-malaysia/",
        serviceType: "Artificial Intelligence Consulting",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Services", url: "/service/" },
        {
          name: "AI Consulting Malaysia",
          url: "/services/ai-consulting-malaysia/",
        },
      ]),
    ],
  },
  "services/seo-services-malaysia": {
    // Offers are generated from lib/seo-pricing.ts so the schema can never quote a
    // price the page does not show.
    schemas: [
      getServiceSchema({
        name: "AI SEO Agency Malaysia — Google and AI Search Visibility",
        description:
          "AI SEO for Malaysian businesses: an approval-gated AI agent makes weekly SEO improvements, and monthly AI visibility tracking checks whether ChatGPT, Gemini and Google AI Overviews name the business.",
        url: "/services/seo-services-malaysia/",
        serviceType: "Search Engine Optimization Service",
        offers: [
          {
            name: `${SEO_PLANS.essential.name} — Monthly`,
            description: `One website, up to ${SEO_PLANS.essential.keywordThemes} keyword themes, AI visibility tracking on ${SEO_PLANS.essential.aiQuestions} customer questions, and a technical SEO proposal every week that the agent applies on approval. Billed month to month.`,
            price: String(SEO_PLANS.essential.monthly),
            priceCurrency: "MYR",
            url: "/services/seo-services-malaysia/#pricing",
          },
          {
            name: `${SEO_PLANS.essential.name} — Annual`,
            description: "Annual AI SEO Essential subscription with a 15% saving.",
            price: String(seoAnnualPrice(SEO_PLANS.essential.monthly)),
            priceCurrency: "MYR",
            url: "/services/seo-services-malaysia/#pricing",
          },
          {
            name: `${SEO_PLANS.growth.name} — Monthly`,
            description: `Everything in Essential, plus a blog post every week published on approval, up to ${SEO_PLANS.growth.keywordThemes} keyword themes and AI visibility tracking on ${SEO_PLANS.growth.aiQuestions} questions. Free five-page website on a ${SEO_FREE_WEBSITE.commitmentMonths}-month plan.`,
            price: String(SEO_PLANS.growth.monthly),
            priceCurrency: "MYR",
            url: "/services/seo-services-malaysia/#pricing",
          },
          {
            name: `${SEO_PLANS.growth.name} — Annual`,
            description: "Annual AI SEO Growth subscription with a 15% saving.",
            price: String(seoAnnualPrice(SEO_PLANS.growth.monthly)),
            priceCurrency: "MYR",
            url: "/services/seo-services-malaysia/#pricing",
          },
          {
            name: `${SEO_PLANS.managed.name} — Monthly (from)`,
            description: `Everything in Growth, plus a named SEO strategist, citation outreach, Google Business Profile management and AI visibility tracking on ${SEO_PLANS.managed.aiQuestions} questions. Scoped per client from this price.`,
            price: String(SEO_PLANS.managed.monthly),
            priceCurrency: "MYR",
            url: "/services/seo-services-malaysia/#pricing",
          },
        ],
      }),
      getWebPageSchema({
        name: "AI SEO Agency Malaysia | Google and AI Search Visibility",
        url: "/services/seo-services-malaysia/",
        description: `AI SEO for Malaysian businesses from ${rm(SEO_PLANS.essential.monthly)}/month, with AI visibility tracking across ChatGPT, Gemini and Google AI Overviews on every plan.`,
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Services", url: "/service/" },
        {
          name: "AI SEO Malaysia",
          url: "/services/seo-services-malaysia/",
        },
      ]),
    ],
  },
  "services/ai-seo-kuala-lumpur": {
    // KL city page. No Offer block: the offers live on the Malaysia page's Service
    // schema (one price list), so they are not duplicated here.
    schemas: [
      getServiceSchema({
        name: "AI SEO Agency Kuala Lumpur — Google and AI Search Visibility",
        description:
          "AI SEO for Kuala Lumpur and Klang Valley businesses: an approval-gated AI agent makes weekly SEO improvements, and monthly AI visibility tracking checks whether ChatGPT, Gemini and Google AI Overviews name the business.",
        url: "/services/ai-seo-kuala-lumpur/",
        serviceType: "Search Engine Optimization Service",
        areaServed: [
          { "@type": "City", name: "Kuala Lumpur" },
          { "@type": "AdministrativeArea", name: "Selangor" },
          { "@type": "Place", name: "Klang Valley" },
        ],
      }),
      getWebPageSchema({
        name: "AI SEO Agency Kuala Lumpur (KL) | Google and ChatGPT Visibility",
        url: "/services/ai-seo-kuala-lumpur/",
        description: `AI SEO agency for Kuala Lumpur and Klang Valley businesses from ${rm(SEO_PLANS.essential.monthly)}/month, with AI visibility tracking across ChatGPT, Gemini and Google AI Overviews on every plan.`,
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Services", url: "/service/" },
        { name: "AI SEO Malaysia", url: "/services/seo-services-malaysia/" },
        { name: "Kuala Lumpur", url: "/services/ai-seo-kuala-lumpur/" },
      ]),
    ],
  },
  "enterprise/sonarqube-malaysia": {
    // SonarQube cluster (spec 2026-09-25). No price in this schema: Cloud Team's
    // public price (C9) lives only in the page copy/llms.txt, Server is quote-only.
    schemas: [
      getServiceSchema({
        name: "SonarQube licences, setup and training in Malaysia",
        description:
          "SonarQube in Malaysia from a Sonar reseller partner: a SAST tool with Team and Enterprise licences, AI CodeFix and agent add-ons, CI/CD setup and HRD Corp training.",
        url: "/enterprise/sonarqube-malaysia/",
        serviceType: "Software reseller and implementation",
        // Malaysia only; Singapore buyers have /singapore/sonarqube/ (Singapore only).
        areaServed: [{ "@type": "Country", name: "Malaysia" }],
        offers: [],
      }),
      getWebPageSchema({
        name: "SonarQube Malaysia | Licences, Setup & Training | Sonar Reseller",
        url: "/enterprise/sonarqube-malaysia/",
        description:
          "SonarQube in Malaysia from a Sonar reseller partner: a SAST tool with Team and Enterprise licences, AI CodeFix and agent add-ons, CI/CD setup and HRD Corp training.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Enterprise", url: "/enterprise/" },
        { name: "SonarQube Malaysia", url: "/enterprise/sonarqube-malaysia/" },
      ]),
    ],
  },
  "services/conversational-ai": {
    schemas: [
      getServiceSchema({
        name: "Conversational AI & WhatsApp Chatbots Malaysia",
        description:
          "Custom WhatsApp and web AI chatbots for Malaysian businesses. Answer customer enquiries 24/7 in Bahasa Malaysia, English, and Chinese, and hand over to your team when it matters.",
        url: "/services/conversational-ai/",
        serviceType: "Conversational AI",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Services", url: "/service/" },
        { name: "Conversational AI", url: "/services/conversational-ai/" },
      ]),
    ],
  },
  "services/vibe-code-rescue": {
    schemas: [
      getWebPageSchema({
        name: "Vibe Code Rescue Malaysia",
        url: "/services/vibe-code-rescue/",
        description:
          "Audit and repair of software built by prompting AI — fixed-scope audit, then a fix measured against written acceptance criteria.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Services", url: "/services/" },
        { name: "Vibe Code Rescue", url: "/services/vibe-code-rescue/" },
      ]),
    ],
  },
  "services/software-development": {
    schemas: [
      serviceSchemas["sds"],
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Services", url: "/service/" },
        {
          name: "Software Development",
          url: "/services/software-development/",
        },
      ]),
    ],
  },
  "services/system-integration": {
    schemas: [
      serviceSchemas["sis"],
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Services", url: "/service/" },
        { name: "System Integration", url: "/services/system-integration/" },
      ]),
    ],
  },
  "services/digital-transformation": {
    schemas: [
      serviceSchemas["dtc"],
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Services", url: "/service/" },
        {
          name: "Digital Transformation",
          url: "/services/digital-transformation/",
        },
      ]),
    ],
  },
  "services/cloud-architecture": {
    schemas: [
      serviceSchemas["cadi"],
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Services", url: "/service/" },
        { name: "Cloud Architecture", url: "/services/cloud-architecture/" },
      ]),
    ],
  },
  "services/website-development": {
    schemas: [
      serviceSchemas["wdh"],
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Services", url: "/service/" },
        {
          name: "Website Development",
          url: "/services/website-development/",
        },
      ]),
    ],
  },
  "services/hardware-software": {
    schemas: [
      serviceSchemas["hsp"],
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Services", url: "/service/" },
        {
          name: "Hardware & Software",
          url: "/services/hardware-software/",
        },
      ]),
    ],
  },
  "services/product-management": {
    schemas: [
      serviceSchemas["pm"],
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Services", url: "/service/" },
        { name: "Product Management", url: "/services/product-management/" },
      ]),
    ],
  },
  "services/smart-manufacturing": {
    schemas: [
      serviceSchemas["sm"],
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Services", url: "/service/" },
        { name: "Smart Manufacturing", url: "/services/smart-manufacturing/" },
      ]),
    ],
  },
  "services/training-education": {
    schemas: [
      serviceSchemas["te"],
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Services", url: "/service/" },
        { name: "Training & Education", url: "/services/training-education/" },
      ]),
    ],
  },

  // Anchor Academy hub — WebPage + BreadcrumbList only; the FAQPage schema is
  // co-located in components-pages/Academy.tsx via <StructuredData type="faq">.
  "academy/claude-training-kuala-lumpur": {
    schemas: [
      getWebPageSchema({
        name: "Claude AI Training Kuala Lumpur & Selangor | Register Interest",
        url: "/academy/claude-training-kuala-lumpur/",
        description:
          "HRD Corp claimable Claude training for teams in KL, Petaling Jaya, Subang and Shah Alam — delivered in your office. Register interest and we open a cohort when the numbers are there.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Anchor Academy", url: "/academy/" },
        { name: "Kuala Lumpur", url: "/academy/claude-training-kuala-lumpur/" },
      ]),
    ],
  },
  "academy/claude-training-penang": {
    schemas: [
      getWebPageSchema({
        name: "Claude AI Training Penang | E&E and Manufacturing Teams",
        url: "/academy/claude-training-penang/",
        description:
          "HRD Corp claimable Claude training for Penang manufacturers — production, quality and supplier workflows. Register interest and we bring a certified trainer when a cohort fills.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Anchor Academy", url: "/academy/" },
        { name: "Penang", url: "/academy/claude-training-penang/" },
      ]),
    ],
  },
  "academy/claude-training-johor": {
    schemas: [
      getWebPageSchema({
        name: "Claude AI Training Johor Bahru | Cross-Border and Logistics Teams",
        url: "/academy/claude-training-johor/",
        description:
          "HRD Corp claimable Claude training for Johor teams — logistics, manufacturing and cross-border operations with Singapore. Register interest and we open a cohort when numbers allow.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Anchor Academy", url: "/academy/" },
        { name: "Johor", url: "/academy/claude-training-johor/" },
      ]),
    ],
  },
  "academy/claude-certification": {
    schemas: [
      getWebPageSchema({
        name: "Claude Certification Malaysia & Singapore",
        url: "/academy/claude-certification/",
        description:
          "The four Anthropic Claude certifications, exam fees, and which track each role should take. HRD Corp claimable prep courses.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Anchor Academy", url: "/academy/" },
        { name: "Claude Certification", url: "/academy/claude-certification/" },
      ]),
    ],
  },
  "academy/chatgpt-training": {
    schemas: [
      getWebPageSchema({
        name: "ChatGPT Training for Teams in Malaysia | HRD Corp Claimable",
        url: "/academy/chatgpt-training/",
        description:
          "Hands-on ChatGPT training for business teams in Malaysia and Singapore: ChatGPT at work, from chat to real output, and working safely with always-on agents in ChatGPT. HRD Corp claimable, with the grant and claim filed for you.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Anchor Academy", url: "/academy/" },
        { name: "ChatGPT Training", url: "/academy/chatgpt-training/" },
      ]),
    ],
  },
  "academy/register-interest": {
    schemas: [
      getWebPageSchema({
        name: "Register Interest — Anchor Academy Courses",
        url: "/academy/register-interest/",
        description:
          "Join the waiting list for Claude certification prep or the Vibe Engineering series. Tell us the course, preferred month, headcount and whether you need the HRD Corp grant and claim filed.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Anchor Academy", url: "/academy/" },
        { name: "Register Interest", url: "/academy/register-interest/" },
      ]),
    ],
  },
  "academy": {
    schemas: [
      getWebPageSchema({
        name: "HRD Corp Approved AI Training Provider | Anchor Academy",
        url: "/academy/",
        description:
          "Instructor-led Claude and Vibe Engineering training delivered in-house in Kuala Lumpur and Singapore by an HRD Corp approved training provider — Claude certification prep, the Vibe Engineering series and custom classes, with the HRD Corp grant and claim filed for you.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Anchor Academy", url: "/academy/" },
      ]),
    ],
  },
"enterprise": {
    schemas: [
      getWebPageSchema({
        name: "IT Supplier & Reseller Malaysia: Enterprise Brand Directory",
        url: "/enterprise/",
        description:
          `Directory of ${enterpriseBrands.length} enterprise brands supplied in Malaysia, grouped as AI and compute, cloud and observability, software licences and hardware, quoted in RM.`,
      }),
      getItemListSchema({
        name: "Enterprise brands supplied in Malaysia",
        description:
          "Every brand Anchor Sprint can quote in Malaysia, grouped on the page as AI and compute, cloud and observability, software licences and hardware.",
        // Neutral wording on purpose: "resells {name}" would read "resells Claude"
        // or "resells OpenAI", which we never say (brands.ts WORDING RULES).
        items: enterpriseBrands.map((b) => ({
          name: b.name,
          url: brandHref(b),
          description: `${b.name} through Anchor Sprint in Malaysia. ${b.blurb}. Quoted in RM.`,
        })),
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Enterprise Solutions", url: "/enterprise/" },
      ]),
    ],
  },
"enterprise/enquiry": {
    schemas: [
      getWebPageSchema({
        name: "Enterprise Enquiry",
        url: "/enterprise/enquiry/",
        description:
          "Request an enterprise software or hardware quotation in RM.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Enterprise Solutions", url: "/enterprise/" },
        { name: "Enterprise Enquiry", url: "/enterprise/enquiry/" },
      ]),
    ],
  },
"enterprise/microsoft-malaysia": {
    schemas: [
      getWebPageSchema({
        name: "Authorised Microsoft Reseller Malaysia",
        url: "/enterprise/microsoft-malaysia/",
        description:
          "Authorised Microsoft reseller in Malaysia, including GitHub Enterprise.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Enterprise Solutions", url: "/enterprise/" },
        { name: "Microsoft Reseller Malaysia", url: "/enterprise/microsoft-malaysia/" },
      ]),
    ],
  },
"enterprise/dell-technologies-malaysia": {
    schemas: [
      getWebPageSchema({
        name: "Authorised Dell Reseller Malaysia",
        url: "/enterprise/dell-technologies-malaysia/",
        description:
          "Authorised Dell reseller in Malaysia: servers, GPU compute, workstations and storage.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Enterprise Solutions", url: "/enterprise/" },
        { name: "Dell Reseller Malaysia", url: "/enterprise/dell-technologies-malaysia/" },
      ]),
    ],
  },
"enterprise/red-hat-malaysia": {
    schemas: [
      getWebPageSchema({
        name: "Authorised Red Hat Reseller Malaysia",
        url: "/enterprise/red-hat-malaysia/",
        description:
          "Authorised Red Hat reseller in Malaysia.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Enterprise Solutions", url: "/enterprise/" },
        { name: "Red Hat Reseller Malaysia", url: "/enterprise/red-hat-malaysia/" },
      ]),
    ],
  },
"enterprise/dgx-spark-tender-malaysia": {
    schemas: [
      getWebPageSchema({
        name: "DGX Spark Tender & RFQ Support Malaysia",
        url: "/enterprise/dgx-spark-tender-malaysia/",
        description:
          "Tender and RFQ support for NVIDIA DGX Spark in Malaysia — baseline specification, equivalent OEM builds, OEM Letter of Authorisation and a quotation in one working day.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Enterprise Solutions", url: "/enterprise/" },
        { name: "DGX Spark Malaysia", url: "/enterprise/dgx-spark-malaysia/" },
        { name: "Tender & RFQ Support", url: "/enterprise/dgx-spark-tender-malaysia/" },
      ]),
    ],
  },
  "enterprise/dgx-spark-malaysia/enquiry": {
    schemas: [
      getWebPageSchema({
        name: "DGX Spark Quotation Enquiry",
        url: "/enterprise/dgx-spark-malaysia/enquiry/",
        description:
          "Request a DGX Spark quotation in Malaysia — OEM build, quantity and workload.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Enterprise Solutions", url: "/enterprise/" },
        { name: "DGX Spark Malaysia", url: "/enterprise/dgx-spark-malaysia/" },
        { name: "Quotation Enquiry", url: "/enterprise/dgx-spark-malaysia/enquiry/" },
      ]),
    ],
  },
  "enterprise/lenovo-thinkstation-pgx-malaysia": {
    schemas: [
      getWebPageSchema({
        name: "Lenovo ThinkStation PGX Malaysia",
        url: "/enterprise/lenovo-thinkstation-pgx-malaysia/",
        description:
          "Lenovo ThinkStation PGX supplied in Malaysia through authorised distribution.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Enterprise Solutions", url: "/enterprise/" },
        { name: "DGX Spark Malaysia", url: "/enterprise/dgx-spark-malaysia/" },
        { name: "Lenovo ThinkStation PGX Malaysia", url: "/enterprise/lenovo-thinkstation-pgx-malaysia/" },
      ]),
    ],
  },
  "enterprise/hp-zgx-nano-malaysia": {
    schemas: [
      getWebPageSchema({
        name: "HP ZGX Nano AI Station Malaysia",
        url: "/enterprise/hp-zgx-nano-malaysia/",
        description:
          "HP ZGX Nano AI Station supplied in Malaysia through authorised distribution.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Enterprise Solutions", url: "/enterprise/" },
        { name: "DGX Spark Malaysia", url: "/enterprise/dgx-spark-malaysia/" },
        { name: "HP ZGX Nano AI Station Malaysia", url: "/enterprise/hp-zgx-nano-malaysia/" },
      ]),
    ],
  },
  "enterprise/asus-ascent-gx10-malaysia": {
    schemas: [
      getWebPageSchema({
        name: "ASUS Ascent GX10 Malaysia",
        url: "/enterprise/asus-ascent-gx10-malaysia/",
        description:
          "ASUS Ascent GX10 supplied in Malaysia through authorised distribution.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Enterprise Solutions", url: "/enterprise/" },
        { name: "DGX Spark Malaysia", url: "/enterprise/dgx-spark-malaysia/" },
        { name: "ASUS Ascent GX10 Malaysia", url: "/enterprise/asus-ascent-gx10-malaysia/" },
      ]),
    ],
  },
  "enterprise/dell-pro-max-gb10-malaysia": {
    schemas: [
      getWebPageSchema({
        name: "Dell Technologies Pro Max with GB10 Malaysia",
        url: "/enterprise/dell-pro-max-gb10-malaysia/",
        description:
          "Dell Technologies Pro Max with GB10 supplied in Malaysia through authorised distribution.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Enterprise Solutions", url: "/enterprise/" },
        { name: "DGX Spark Malaysia", url: "/enterprise/dgx-spark-malaysia/" },
        { name: "Dell Technologies Pro Max with GB10 Malaysia", url: "/enterprise/dell-pro-max-gb10-malaysia/" },
      ]),
    ],
  },
  "enterprise/acer-veriton-gn100-malaysia": {
    schemas: [
      getWebPageSchema({
        name: "Acer Veriton GN100 Malaysia",
        url: "/enterprise/acer-veriton-gn100-malaysia/",
        description:
          "Acer Veriton GN100 supplied in Malaysia through authorised distribution.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Enterprise Solutions", url: "/enterprise/" },
        { name: "DGX Spark Malaysia", url: "/enterprise/dgx-spark-malaysia/" },
        { name: "Acer Veriton GN100 Malaysia", url: "/enterprise/acer-veriton-gn100-malaysia/" },
      ]),
    ],
  },
  "enterprise/dgx-spark-malaysia": {
    schemas: [
      getWebPageSchema({
        name: "DGX Spark Malaysia",
        url: "/enterprise/dgx-spark-malaysia/",
        description:
          "Authorised NVIDIA DGX Spark reseller in Malaysia, supplying the Founders Edition and every OEM build.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Enterprise Solutions", url: "/enterprise/" },
        { name: "DGX Spark Malaysia", url: "/enterprise/dgx-spark-malaysia/" },
      ]),
    ],
  },
  "enterprise/nvidia-malaysia": {
    schemas: [
      getWebPageSchema({
        name: "NVIDIA Supplier Malaysia",
        url: "/enterprise/nvidia-malaysia/",
        description:
          "NVIDIA GPUs, AI Enterprise licensing and certified systems supplied in Malaysia through authorised distribution.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Enterprise Solutions", url: "/enterprise/" },
        { name: "NVIDIA Supplier Malaysia", url: "/enterprise/nvidia-malaysia/" },
      ]),
    ],
  },
  "enterprise/nvidia-malaysia/enquiry": {
    schemas: [
      getWebPageSchema({
        name: "NVIDIA GPU Quotation Enquiry",
        url: "/enterprise/nvidia-malaysia/enquiry/",
        description:
          "Request an NVIDIA GPU quotation in Malaysia — data-centre GPUs require end-user documentation before pricing is released.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Enterprise Solutions", url: "/enterprise/" },
        { name: "NVIDIA Supplier Malaysia", url: "/enterprise/nvidia-malaysia/" },
        { name: "Quotation Enquiry", url: "/enterprise/nvidia-malaysia/enquiry/" },
      ]),
    ],
  },

  "solutions/agent-use-cases": {
    schemas: [
      getWebPageSchema({
        name: "AI Agent Use Cases for Malaysian Business",
        url: "/solutions/agent-use-cases/",
        description:
          "A directory of AI agent use cases — the manual workflow, the agentic redesign, how we build it on Claude, and how we prove it works.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Solutions", url: "/solutions/" },
        { name: "Agent use cases", url: "/solutions/agent-use-cases/" },
      ]),
      getItemListSchema({
        name: "AI Agent Use Cases",
        description:
          "AI agent use cases Anchor Sprint designs and builds for Malaysian business",
        items: [
          {
            name: "Credit evaluation agent",
            url: "/solutions/agent-use-cases/credit-evaluation/",
          },
          { name: "Order processing agent (WhatsApp)", url: "/solutions/agent-use-cases/" },
          { name: "Product enquiry agent (WhatsApp)", url: "/solutions/agent-use-cases/" },
          { name: "Claims processing agent", url: "/solutions/agent-use-cases/" },
          { name: "Invoice & PO matching agent", url: "/solutions/agent-use-cases/" },
          { name: "KYC & onboarding agent", url: "/solutions/agent-use-cases/" },
          { name: "Contract review agent", url: "/solutions/agent-use-cases/" },
          { name: "Support ticket triage agent", url: "/solutions/agent-use-cases/" },
          {
            name: "Candidate credential check agent",
            url: "/solutions/agent-use-cases/",
          },
        ],
      }),
    ],
  },

  "solutions/agent-use-cases/credit-evaluation": {
    schemas: [
      getWebPageSchema({
        name: "Credit Evaluation Agent: Automate Loan Document Review",
        url: "/solutions/agent-use-cases/credit-evaluation/",
        description:
          "How we design and build a credit evaluation agent that reads and cross-checks loan documents and flags discrepancies — human-in-the-loop, BNM/PDPA-aware.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Solutions", url: "/solutions/" },
        { name: "Agent use cases", url: "/solutions/agent-use-cases/" },
        {
          name: "Credit evaluation",
          url: "/solutions/agent-use-cases/credit-evaluation/",
        },
      ]),
    ],
  },

  "solutions/agent-use-cases/order-processing": {
    schemas: [
      getWebPageSchema({
        name: "WhatsApp Order Processing Agent: Automate Customer Orders",
        url: "/solutions/agent-use-cases/order-processing/",
        description:
          "How we design and build a WhatsApp order processing agent that captures items, confirms the total, and drops a clean order into your POS — human-in-the-loop, for Malaysian F&B and retail.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Solutions", url: "/solutions/" },
        { name: "Agent use cases", url: "/solutions/agent-use-cases/" },
        {
          name: "Order processing",
          url: "/solutions/agent-use-cases/order-processing/",
        },
      ]),
    ],
  },

  "solutions/agent-use-cases/product-enquiry": {
    schemas: [
      getWebPageSchema({
        name: "WhatsApp Product Enquiry Agent: Instant Answers",
        url: "/solutions/agent-use-cases/product-enquiry/",
        description: "Answer product questions instantly on WhatsApp — availability, specs and price — from your own catalogue.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Solutions", url: "/solutions/" },
        { name: "Agent use cases", url: "/solutions/agent-use-cases/" },
        { name: "Product enquiry", url: "/solutions/agent-use-cases/product-enquiry/" },
      ]),
    ],
  },
  "solutions/agent-use-cases/insurance-claims": {
    schemas: [
      getWebPageSchema({
        name: "Insurance Claims Agent: Faster Claim Checks",
        url: "/solutions/agent-use-cases/insurance-claims/",
        description: "Match a claim against the policy, the report and the receipts — and flag what doesn't add up.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Solutions", url: "/solutions/" },
        { name: "Agent use cases", url: "/solutions/agent-use-cases/" },
        { name: "Insurance claims", url: "/solutions/agent-use-cases/insurance-claims/" },
      ]),
    ],
  },
  "solutions/agent-use-cases/invoice-matching": {
    schemas: [
      getWebPageSchema({
        name: "Invoice Matching Agent: Automated 3-Way PO Match",
        url: "/solutions/agent-use-cases/invoice-matching/",
        description: "Three-way match of purchase order, delivery order and invoice before a payment goes out.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Solutions", url: "/solutions/" },
        { name: "Agent use cases", url: "/solutions/agent-use-cases/" },
        { name: "Invoice matching", url: "/solutions/agent-use-cases/invoice-matching/" },
      ]),
    ],
  },
  "solutions/agent-use-cases/leave-management": {
    schemas: [
      getWebPageSchema({
        name: "Leave & Shift-Cover Agent: Automate Leave & MC",
        url: "/solutions/agent-use-cases/leave-management/",
        description: "Take leave and MC requests, check policy and balances, route for approval, and find qualified shift cover.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Solutions", url: "/solutions/" },
        { name: "Agent use cases", url: "/solutions/agent-use-cases/" },
        { name: "Leave & shift cover", url: "/solutions/agent-use-cases/leave-management/" },
      ]),
    ],
  },
  "solutions/agent-use-cases/bank-reconciliation": {
    schemas: [
      getWebPageSchema({
        name: "Bank Reconciliation Agent: Automate the Rec",
        url: "/solutions/agent-use-cases/bank-reconciliation/",
        description: "Reconcile the bank statement against the cash book both ways, post bank-only items, and flag genuine discrepancies.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Solutions", url: "/solutions/" },
        { name: "Agent use cases", url: "/solutions/agent-use-cases/" },
        { name: "Bank reconciliation", url: "/solutions/agent-use-cases/bank-reconciliation/" },
      ]),
    ],
  },
  "solutions/agent-use-cases/cash-application": {
    schemas: [
      getWebPageSchema({
        name: "Cash Application Agent: Auto-Apply Receipts",
        url: "/solutions/agent-use-cases/cash-application/",
        description: "Match incoming customer receipts and POS settlements to open invoices and clear them automatically.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Solutions", url: "/solutions/" },
        { name: "Agent use cases", url: "/solutions/agent-use-cases/" },
        { name: "Cash application", url: "/solutions/agent-use-cases/cash-application/" },
      ]),
    ],
  },
  "solutions/agent-use-cases/kyc-onboarding": {
    schemas: [
      getWebPageSchema({
        name: "KYC & Onboarding Agent: Automate Checks",
        url: "/solutions/agent-use-cases/kyc-onboarding/",
        description: "Check that identity documents, proof of address and application details are consistent.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Solutions", url: "/solutions/" },
        { name: "Agent use cases", url: "/solutions/agent-use-cases/" },
        { name: "Onboarding & KYC", url: "/solutions/agent-use-cases/kyc-onboarding/" },
      ]),
    ],
  },
  "solutions/agent-use-cases/contract-review": {
    schemas: [
      getWebPageSchema({
        name: "Contract Review Agent: Spot Changed Clauses Fast",
        url: "/solutions/agent-use-cases/contract-review/",
        description: "Compare a signed contract against the agreed template and surface the clauses that changed.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Solutions", url: "/solutions/" },
        { name: "Agent use cases", url: "/solutions/agent-use-cases/" },
        { name: "Contract review", url: "/solutions/agent-use-cases/contract-review/" },
      ]),
    ],
  },
  "solutions/agent-use-cases/support-triage": {
    schemas: [
      getWebPageSchema({
        name: "Support Triage Agent: Auto-Route Tickets",
        url: "/solutions/agent-use-cases/support-triage/",
        description: "Read, categorise and route incoming tickets, resolving the routine ones on the spot.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Solutions", url: "/solutions/" },
        { name: "Agent use cases", url: "/solutions/agent-use-cases/" },
        { name: "Support triage", url: "/solutions/agent-use-cases/support-triage/" },
      ]),
    ],
  },
  "solutions/agent-use-cases/knowledge-agent": {
    schemas: [
      getWebPageSchema({
        name: "Internal Knowledge Agent: Staff Q&A from Your Docs",
        url: "/solutions/agent-use-cases/knowledge-agent/",
        description: "Answer staff questions from your own SOPs, policies and handbooks — with the source cited, and honest when it doesn't know.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Solutions", url: "/solutions/" },
        { name: "Agent use cases", url: "/solutions/agent-use-cases/" },
        { name: "Internal knowledge agent", url: "/solutions/agent-use-cases/knowledge-agent/" },
      ]),
    ],
  },
  "solutions/agent-use-cases/hr-verification": {
    schemas: [
      getWebPageSchema({
        name: "HR Verification Agent: Credential Checks",
        url: "/solutions/agent-use-cases/hr-verification/",
        description: "Verify that a candidate's certificates, references and stated history line up — before the offer goes out.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Solutions", url: "/solutions/" },
        { name: "Agent use cases", url: "/solutions/agent-use-cases/" },
        { name: "HR verification", url: "/solutions/agent-use-cases/hr-verification/" },
      ]),
    ],
  },
  "solutions/agent-use-cases/corporate-comms": {
    schemas: [
      getWebPageSchema({
        name: "Corporate Communications Consistency Agent",
        url: "/solutions/agent-use-cases/corporate-comms/",
        description: "Turn one approved corporate message into on-brand, fact-consistent versions in every language — every figure and claim checked back to the source, and a human signs off before release.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Solutions", url: "/solutions/" },
        { name: "Agent use cases", url: "/solutions/agent-use-cases/" },
        { name: "Corporate communications consistency", url: "/solutions/agent-use-cases/corporate-comms/" },
      ]),
    ],
  },
"solutions/ai-agents-for-logistics": {
    schemas: [
      getWebPageSchema({
        name: "AI Agents for Logistics in Malaysia & Singapore",
        url: "/solutions/ai-agents-for-logistics/",
        description: "Custom AI agents for order processing, 3-way invoice matching, contract review and support across the Malaysia–Singapore supply chain.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Solutions", url: "/solutions/" },
        { name: "AI agents for logistics", url: "/solutions/ai-agents-for-logistics/" },
      ]),
    ],
  },
  "solutions/ai-agents-for-retail": {
    schemas: [
      getWebPageSchema({
        name: "AI Agents for Retail & F&B in Malaysia & Singapore",
        url: "/solutions/ai-agents-for-retail/",
        description: "Custom AI agents that capture WhatsApp orders, answer product enquiries and keep messaging consistent for retail & F&B in Malaysia and Singapore.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Solutions", url: "/solutions/" },
        { name: "AI agents for retail & F&B", url: "/solutions/ai-agents-for-retail/" },
      ]),
    ],
  },
  "solutions/agent-rescue": {
    schemas: [
      getServiceSchema({
        name: "AI Agent Rescue — Fix a Broken AI Agent",
        description: "A free audit and honest rectify-or-rebuild verdict for AI agents that a vendor delivered but that don't work. Handover from RM 2,000, plus ongoing maintenance. Malaysia & Singapore.",
        url: "/solutions/agent-rescue/",
        serviceType: "AI Agent Remediation",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Solutions", url: "/solutions/" },
        { name: "AI Agent Rescue", url: "/solutions/agent-rescue/" },
      ]),
    ],
  },
  singapore: {
    schemas: [
      getWebPageSchema({
        name: "Claude AI & Agentic Development in Singapore",
        url: "/singapore/",
        description:
          "Claude implementation, partner services, training and managed operations in Singapore, Certified in the Claude Partner Network.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Singapore", url: "/singapore/" },
      ]),
      singaporeLocalBusinessSchema,
    ],
  },
  "singapore/claude-implementation": {
    schemas: [
      singaporeLocalBusinessSchema,
      getWebPageSchema({
        name: "Claude Implementation Singapore",
        url: "/singapore/claude-implementation/",
        description:
          "Implement Claude and agentic AI in Singapore end to end — POC to production, PDPC + IMDA governed, costed in SGD.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Singapore", url: "/singapore/" },
        {
          name: "Claude implementation",
          url: "/singapore/claude-implementation/",
        },
      ]),
      getServiceSchema({
        name: "Claude Implementation Singapore",
        description:
          "End-to-end Claude and agentic AI implementation for Singapore businesses — POC to production, integrated, PDPC + IMDA governed.",
        url: "/singapore/claude-implementation/",
        serviceType: "AI Implementation & Deployment",
        areaServed: [{ "@type": "Country", name: "Singapore" }],
      }),
    ],
  },
  "singapore/claude-partner": {
    schemas: [
      singaporeLocalBusinessSchema,
      getWebPageSchema({
        name: "Claude Partner Singapore | Anthropic Claude Partner Network",
        url: "/singapore/claude-partner/",
        description:
          "Anchor Sprint is a homegrown Anthropic Claude Partner Network member serving Singapore from Suntec — a full team of Claude Certified Architects (CCAR-F) building production Claude agents with the Agent SDK, Claude Code, and MCP. Managed service, consulting, implementation, RAG and custom agents, and on-demand FDE teams with local SGD billing.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Singapore", url: "/singapore/" },
        { name: "Claude partner", url: "/singapore/claude-partner/" },
      ]),
      getServiceSchema({
        name: "Claude Partner Services Singapore",
        description:
          "Anchor Sprint is a homegrown Anthropic Claude Partner Network member serving Singapore from Suntec — a full team of Claude Certified Architects (CCAR-F) building production Claude agents with the Agent SDK, Claude Code, and MCP. Managed service, consulting, implementation, RAG and custom agents, and on-demand FDE teams with local SGD billing.",
        url: "/singapore/claude-partner/",
        serviceType: "AI Agent Development",
        areaServed: [{ "@type": "Country", name: "Singapore" }],
      }),
    ],
  },
  "singapore/claude-training": {
    schemas: [
      singaporeLocalBusinessSchema,
      getWebPageSchema({
        name: "Claude Training Singapore",
        url: "/singapore/claude-training/",
        description:
          "Practical Claude and agentic-AI training for Singapore teams — prompting, agents and governed use under PDPC.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Singapore", url: "/singapore/" },
        { name: "Claude training", url: "/singapore/claude-training/" },
      ]),
      getServiceSchema({
        name: "Claude Training Singapore",
        description:
          // Funding clause dropped 2026-08-08 — we are not an SSG Registered
          // Training Provider, and structured data is the last place to carry an
          // inferable claim. See the page FAQ for the stated position.
          "Claude and agentic-AI training for Singapore teams — prompting, agent design, and PDPC-compliant use, delivered by Claude Certified Architects.",
        url: "/singapore/claude-training/",
        serviceType: "AI Training & Enablement",
        areaServed: [{ "@type": "Country", name: "Singapore" }],
      }),
    ],
  },
  "singapore/sonarqube": {
    schemas: [
      singaporeLocalBusinessSchema,
      getWebPageSchema({
        name: "SonarQube Singapore",
        url: "/singapore/sonarqube/",
        description:
          "Sonar reseller partner in Singapore: SonarQube licences in SGD, a SAST tool for Singapore teams, the Remediation Agent and add-ons, with setup and training.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Singapore", url: "/singapore/" },
        { name: "SonarQube", url: "/singapore/sonarqube/" },
      ]),
      getServiceSchema({
        name: "SonarQube Singapore",
        description:
          "SonarQube licences quoted in SGD, SAST and Advanced Security, the SonarQube Remediation Agent and other add-ons, CI/CD pipeline setup and developer training in Singapore from a Sonar reseller partner.",
        url: "/singapore/sonarqube/",
        serviceType: "Software reseller and implementation",
        areaServed: [{ "@type": "Country", name: "Singapore" }],
      }),
    ],
  },
  // No Offer schema: there are no approved SGD prices, and the page quotes after
  // the free AI visibility check.
  "singapore/ai-seo": {
    schemas: [
      singaporeLocalBusinessSchema,
      getWebPageSchema({
        name: "AI SEO Singapore",
        url: "/singapore/ai-seo/",
        description:
          `AI SEO for Singapore businesses from ${sgd(SEO_PLANS_SGD_MONTHLY.essential)}/month: weekly website fixes by an AI agent and monthly AI visibility tracking in ChatGPT, Gemini and Google AI Overviews.`,
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Singapore", url: "/singapore/" },
        { name: "AI SEO", url: "/singapore/ai-seo/" },
      ]),
      getServiceSchema({
        name: "AI SEO Singapore",
        description:
          "Google SEO and AI visibility for Singapore businesses: an approval-gated AI agent improves the website weekly, and every month we check whether ChatGPT, Gemini and Google AI Overviews name the business.",
        url: "/singapore/ai-seo/",
        serviceType: "Search engine optimisation",
        areaServed: [{ "@type": "Country", name: "Singapore" }],
        // Generated from seo-pricing.ts so the schema never quotes a price the page
        // does not show.
        offers: [
          {
            name: `${SEO_PLANS.essential.name} — Monthly`,
            description: "One website, weekly technical SEO proposals applied on approval, and monthly AI visibility tracking. Billed month to month.",
            price: String(SEO_PLANS_SGD_MONTHLY.essential),
            priceCurrency: "SGD",
            url: "/singapore/ai-seo/#plans",
          },
          {
            name: `${SEO_PLANS.growth.name} — Monthly`,
            description: "Everything in Essential, plus a blog post every week published on approval. Free five-page website on a 12-month plan.",
            price: String(SEO_PLANS_SGD_MONTHLY.growth),
            priceCurrency: "SGD",
            url: "/singapore/ai-seo/#plans",
          },
          {
            name: `${SEO_PLANS.managed.name} — Monthly (from)`,
            description: "Everything in Growth, plus a named SEO strategist, citation outreach and Google Business Profile management. Scoped per client from this price.",
            price: String(SEO_PLANS_SGD_MONTHLY.managed),
            priceCurrency: "SGD",
            url: "/singapore/ai-seo/#plans",
          },
        ],
      }),
    ],
  },
  "singapore/claude-managed-service": {
    schemas: [
      singaporeLocalBusinessSchema,
      getWebPageSchema({
        name: "Claude Managed Service Singapore",
        url: "/singapore/claude-managed-service/",
        description:
          "Managed Claude operations in Singapore — monitoring, evals, governance and SLA-backed support.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Singapore", url: "/singapore/" },
        {
          name: "Managed service",
          url: "/singapore/claude-managed-service/",
        },
      ]),
      getServiceSchema({
        name: "Claude Managed Service Singapore",
        description:
          "Managed Claude operations for Singapore — monitoring, regression evals, governance and SLA-backed support, costed in SGD.",
        url: "/singapore/claude-managed-service/",
        serviceType: "AI Managed Service",
        areaServed: [{ "@type": "Country", name: "Singapore" }],
      }),
    ],
  },
  "tools/ai-visibility-check": {
    // The check is the free entry point to the AI SEO service: no Offer/price here,
    // because the page sells no priced product. FAQPage is rendered in the
    // component (so it also ships on /ms and /zh).
    schemas: [
      getServiceSchema({
        name: "Free AI Visibility Check",
        description:
          "A free check of whether ChatGPT, Gemini and Google AI Overviews name a business: five customer questions, who gets named instead, which sources are cited, what is holding the website back, and a recommended plan. No obligation.",
        url: "/tools/ai-visibility-check/",
        serviceType: "AI Search Visibility Audit",
      }),
      getWebPageSchema({
        name: "Free AI Visibility Check: Is Your Business on ChatGPT?",
        url: "/tools/ai-visibility-check/",
        description:
          "Free AI visibility check for Malaysian businesses: five customer questions put to ChatGPT, Gemini and Google AI Overviews, with who they name instead.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Free AI visibility check", url: "/tools/ai-visibility-check/" },
      ]),
    ],
  },
  "tools/claude-cost-calculator": {
    schemas: [
      getWebPageSchema({
        name: "Claude Pricing Calculator: Team vs Enterprise Cost",
        url: "/tools/claude-cost-calculator/",
        description:
          "Free Claude pricing calculator comparing Team vs Enterprise cost over 3 years — set team size and usage, see per-seat + API cost in RM or USD, and download an Excel.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Tools", url: "/tools/claude-cost-calculator/" },
        {
          name: "Claude cost calculator",
          url: "/tools/claude-cost-calculator/",
        },
      ]),
    ],
  },

  "careers": {
    schemas: [
      getWebPageSchema({
        name: "Careers at Anchor Sprint — AI Engineering & GTM Jobs in Malaysia",
        url: "/careers/",
        description:
          "Join Anchor Sprint in Shah Alam and build AI that Malaysian businesses actually use. Open roles in AI engineering, GTM and business development, plus internships.",
        type: "CollectionPage",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Careers", url: "/careers/" },
      ]),
      // JobPosting lives on each /careers/<slug>/ page (Google: one job per
      // page, never on a listing page). This page lists them.
      getItemListSchema({
        name: "Open roles at Anchor Sprint",
        items: careerRoles.map((role) => ({
          name: role.title,
          url: careerRolePath(role),
          description: role.tagline,
        })),
      }),
    ],
  },

  // =========================================================================
  // COMPANY PAGES
  // =========================================================================
  "our-people": {
    schemas: [
      {
        ...getWebPageSchema({
          name: "Our People: Jazz Tong and Liew Jee Fong",
          url: "/our-people/",
          type: "AboutPage",
          description: "Meet Anchor Sprint co-founders Jazz Tong, CEO, and Liew Jee Fong, Chief Business Officer.",
        }),
        mainEntity: founders.map((founder) => ({
          "@id": `https://www.anchorsprint.com/our-people/#${founder.id}`,
        })),
        primaryImageOfPage: {
          "@type": "ImageObject",
          url: "https://www.anchorsprint.com/images/people/our-people-og.jpg",
          width: 1200,
          height: 630,
        },
      },
      ...founders.map((founder, index) => ({
        "@context": "https://schema.org",
        "@type": "Person",
        "@id": `https://www.anchorsprint.com/our-people/#${founder.id}`,
        name: founder.name,
        jobTitle: founder.jobTitle,
        description: peopleStudioCopy.en.profiles[index].intro,
        knowsAbout: peoplePageCopy.en.profiles[index].topics,
        image: `https://www.anchorsprint.com${founder.portrait}`,
        hasCredential: founder.credentials.map((name) => ({ "@type": "EducationalOccupationalCredential", name })),
        url: `https://www.anchorsprint.com/our-people/#${founder.id}`,
        sameAs: [founder.linkedIn, ...("medium" in founder ? [founder.medium] : [])],
        worksFor: { "@id": "https://www.anchorsprint.com/#organization" },
      })),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Company", url: "/company/" },
        { name: "Our People", url: "/our-people/" },
      ]),
    ],
  },
  "about-us": {
    schemas: [
      getWebPageSchema({
        name: "About Us - Anchor Sprint Sdn Bhd",
        url: "/about-us/",
        description:
          "Anchor Sprint Sdn Bhd specializes in tailored technology solutions — software development, system integration, cloud architecture, and AI innovation — backed by over 16 years of industry experience.",
        type: "AboutPage",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "About Us", url: "/about-us/" },
      ]),
    ],
  },
  company: {
    schemas: [
      getCollectionPageSchema({
        name: "Company - Anchor Sprint Sdn Bhd",
        url: "/company/",
        description:
          "Explore Anchor Sprint's company profile — our story, corporate values, mission, industries served, and strategic partners.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Company", url: "/company/" },
      ]),
    ],
  },
  "corporate-mission": {
    schemas: [
      getWebPageSchema({
        name: "Corporate Mission - Anchor Sprint Sdn Bhd",
        url: "/corporate-mission/",
        description:
          "Anchor Sprint's mission: empower businesses through innovative technology solutions that drive growth, enhance efficiency, and create sustainable competitive advantages.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Company", url: "/company/" },
        { name: "Corporate Mission", url: "/corporate-mission/" },
      ]),
    ],
  },
  "privacy-policy": {
    schemas: [
      getWebPageSchema({
        name: "Privacy Notice - Anchor Sprint",
        url: "/privacy-policy/",
        description:
          "PDPA privacy notice for Anchor Sprint Sdn Bhd and Anchor Sprint Pte. Ltd.: personal data collected through the website and forms, purposes, optional marketing consent, disclosure, cross-border transfer, retention, security, data subject rights, and the read-only QuickBooks Connector for Claude.",
        dateModified: "2026-11-05",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Privacy Notice", url: "/privacy-policy/" },
      ]),
    ],
  },
  "corporate-values": {
    schemas: [
      getWebPageSchema({
        name: "Corporate Values - Anchor Sprint Sdn Bhd",
        url: "/corporate-values/",
        description:
          "The six core values that define Anchor Sprint: Client-Centricity, Innovation, Excellence, Integrity, Collaboration, and Adaptability.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Company", url: "/company/" },
        { name: "Corporate Values", url: "/corporate-values/" },
      ]),
      getItemListSchema({
        name: "Anchor Sprint Core Values",
        items: [
          {
            name: "Client-Centricity",
            description:
              "We put our clients at the center of everything we do, listening deeply to their needs and working collaboratively to deliver solutions that drive meaningful business outcomes.",
          },
          {
            name: "Innovation",
            description:
              "We embrace creative thinking and continuous exploration of emerging technologies to develop innovative solutions that address complex business challenges.",
          },
          {
            name: "Excellence",
            description:
              "We are committed to delivering excellence in everything we do, maintaining the highest standards of quality, professionalism, and technical expertise.",
          },
          {
            name: "Integrity",
            description:
              "We operate with unwavering integrity, building trust through honesty, transparency, and ethical business practices.",
          },
          {
            name: "Collaboration",
            description:
              "We believe in the power of collaboration, fostering a culture of teamwork, knowledge sharing, and mutual respect.",
          },
          {
            name: "Adaptability",
            description:
              "We embrace change and maintain the agility to adapt quickly to evolving client needs, market conditions, and technological advancements.",
          },
        ],
      }),
    ],
  },

  // =========================================================================
  // CONTACT
  // =========================================================================
  contact: {
    schemas: [
            getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Contact", url: "/contact/" },
      ]),
    ],
  },

  // =========================================================================
  // BLOG LISTING
  // =========================================================================
  blog: {
    schemas: [
      getCollectionPageSchema({
        name: "Blog | Anchor Sprint",
        url: "/blog/",
        description:
          "Articles on AI, cloud, web development, and digital transformation for Malaysian businesses.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Blog", url: "/blog/" },
      ]),
    ],
  },

  // =========================================================================
  // INDUSTRIES
  // =========================================================================
  "industries-we-serve": {
    schemas: [
      getWebPageSchema({
        name: "Industries We Serve | Anchor Sprint",
        url: "/industries-we-serve/",
        description:
          "Anchor Sprint serves manufacturing, retail, education, technology, construction, and hospitality industries with tailored digital solutions.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Industries We Serve", url: "/industries-we-serve/" },
      ]),
      getItemListSchema({
        name: "Industries Served by Anchor Sprint",
        items: [
          {
            name: "Manufacturing",
            description:
              "Industrial IoT, supply chain optimization, predictive maintenance, quality control",
          },
          {
            name: "Wholesale & Retail Trading",
            description:
              "B2B/B2C commerce platforms, inventory & warehouse systems, trade partner integration",
          },
          {
            name: "Education",
            description:
              "Learning management systems, student information systems, book inventory management",
          },
          {
            name: "Technology",
            description:
              "Cloud migration, API development, DevOps, SaaS platform development",
          },
          {
            name: "Construction",
            description:
              "Project management software, safety compliance systems, supply chain optimization",
          },
          {
            name: "Services & Hospitality",
            description:
              "Booking systems, customer experience platforms, property management, loyalty programs",
          },
        ],
      }),
    ],
  },

  // =========================================================================
  // PARTNERS
  // =========================================================================
  "our-partners": {
    schemas: [
      getCollectionPageSchema({
        name: "Our Partners | Anchor Sprint",
        url: "/our-partners/",
        description:
          "Technology partners: Anthropic (Certified in the Claude Partner Network); authorised reseller for Microsoft (including GitHub Enterprise), Lenovo, NVIDIA DGX Spark, Red Hat, Alibaba Cloud and TrueWatch; Google Workspace reseller; SonarQube (Sonar reseller partner); Adobe Certified Reseller; Dell Technologies; AWS.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Our Partners", url: "/our-partners/" },
      ]),
    ],
  },
  "enterprise/adobe-malaysia": {
    schemas: [
            getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Our Partners", url: "/our-partners/" },
        {
          name: "Adobe Reseller Selangor",
          url: "/enterprise/adobe-malaysia/",
        },
      ]),
    ],
  },
  "anthropic-claude-partner-malaysia-singapore": {
    schemas: [
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        {
          name: "Why We Build on Claude",
          url: "/anthropic-claude-partner-malaysia-singapore/",
        },
      ]),
      getWebPageSchema({
        name: "Why We Build on Claude",
        description:
          "Why Anchor Sprint builds on Claude, the Claude Certified Architects who deliver it, and a home-grown engineering team working across Malaysia and Singapore.",
        url: "/anthropic-claude-partner-malaysia-singapore/",
      }),
    ],
  },
  // The page carried only a BreadcrumbList, so the sole machine-readable claim
  // about it came from the root layout's Malaysian Organization/LocalBusiness —
  // which leads areaServed with Selangor and never names a Claude capability.
  // Agmo's equivalent page ships Organization + memberOf(Claude Partner Network
  // -> Anthropic) + areaServed Country, and its AI Overview sentence is markedly
  // richer than ours. This adds the same machine-readable footing. Treat it as
  // hygiene, not the lever: the extractable copy is what moves the summary.
  "quickbooks-connector": {
    schemas: [
      getWebPageSchema({
        name: "Anchor Sprint QuickBooks Connector for Claude",
        url: "/quickbooks-connector/",
        description:
          "Read-only connector that lets Claude Desktop read QuickBooks Online P&L reports, GST tax codes and transaction detail, and bank transactions, running locally on the customer's PC.",
        dateModified: "2026-11-05",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "QuickBooks Connector", url: "/quickbooks-connector/" },
      ]),
    ],
  },
  "quickbooks-connector/terms": {
    schemas: [
      getWebPageSchema({
        name: "QuickBooks Connector End-User Licence Agreement - Anchor Sprint",
        url: "/quickbooks-connector/terms/",
        description:
          "End-User Licence Agreement for the Anchor Sprint QuickBooks Connector for Claude, licensed by Anchor Sprint Pte. Ltd.",
        dateModified: "2026-11-05",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "QuickBooks Connector", url: "/quickbooks-connector/" },
        { name: "Terms", url: "/quickbooks-connector/terms/" },
      ]),
    ],
  },
  "invest": {
    schemas: [
      getWebPageSchema({
        name: "Investment & Partnership — Anchor Sprint",
        url: "/invest/",
        description:
          "Information for investors and partners: what Anchor Sprint builds, what is already true, and how to start a conversation.",
        dateModified: "2026-08-10",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Investment & Partnership", url: "/invest/" },
      ]),
    ],
  },
  "partners/anthropic-claude-partner": {
    schemas: [
      // WebPage added so the /ms/ and /zh/ twins can carry a localized
      // name/description (localizePageSchemas only rewrites WebPage nodes).
      getWebPageSchema({
        name: "Claude Partner Malaysia | Anthropic Claude Partner Network",
        url: "/partners/anthropic-claude-partner/",
        description:
          "Anchor Sprint is Malaysia's homegrown Anthropic Claude Partner Network member — a full team of Claude Certified Architects (CCAR-F) building production Claude agents with the Agent SDK, Claude Code, and MCP. Managed service, consulting, implementation, RAG and custom agents, and on-demand FDE teams with local MYR billing and HRDF-claimable training.",
      }),
      getServiceSchema({
        name: "Claude Managed Service, Agent Development and Training",
        description:
          "Anchor Sprint is Malaysia's homegrown Anthropic Claude Partner Network member — a full team of Claude Certified Architects (CCAR-F) building production Claude agents with the Agent SDK, Claude Code, and MCP. Managed service, consulting, implementation, RAG and custom agents, and on-demand FDE teams with local MYR billing and HRDF-claimable training.",
        url: "/partners/anthropic-claude-partner/",
        serviceType: "AI Agent Development",
        areaServed: [
          { "@type": "Country", name: "Malaysia" },
          { "@type": "Country", name: "Singapore" },
        ],
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Our Partners", url: "/our-partners/" },
        {
          name: "Anthropic Claude Partner",
          url: "/partners/anthropic-claude-partner/",
        },
      ]),
    ],
  },
  // OpenAI Select Partner page (2026-10-08). Status wording exactly "OpenAI
  // Select Partner"; Offers stay generic with no price (Jazz). Seat/Azure
  // wording mirrors src/content/enterprise/brands.ts.
  "partners/openai-partner": {
    schemas: [
      getWebPageSchema({
        name: "OpenAI Select Partner Malaysia",
        url: "/partners/openai-partner/",
        description:
          "Anchor Sprint is an OpenAI Select Partner in Malaysia and Singapore: ChatGPT Business and Enterprise rollout, agents and integrations on the OpenAI API, PDPA governance, and HRD Corp claimable ChatGPT training for teams.",
      }),
      getServiceSchema({
        name: "ChatGPT Enterprise Rollout, OpenAI API Development and ChatGPT Training",
        description:
          "ChatGPT Business and Enterprise rollout and admin setup, agents and integrations built on the OpenAI API, governance for PDPA, and HRD Corp claimable ChatGPT team training for organisations in Malaysia and Singapore.",
        url: "/partners/openai-partner/",
        serviceType: "AI Implementation",
        areaServed: [
          { "@type": "Country", name: "Malaysia" },
          { "@type": "Country", name: "Singapore" },
        ],
        offers: [
          {
            name: "ChatGPT Business and Enterprise rollout",
            description: "Workspace and admin setup, access, usage policy and team onboarding.",
          },
          {
            name: "OpenAI API agents and integrations",
            description: "Agents and integrations on the OpenAI API, scored against the manual process before launch.",
          },
          {
            name: "HRD Corp claimable ChatGPT training",
            description: "Hands-on ChatGPT workshops for teams, with the HRD Corp claim filed for you.",
          },
        ],
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Our Partners", url: "/our-partners/" },
        { name: "OpenAI Select Partner", url: "/partners/openai-partner/" },
      ]),
    ],
  },
  "service-details": {
    schemas: [
      getWebPageSchema({
        name: "Service Details | Enterprise Solutions Malaysia",
        url: "/service-details/",
        description:
          "Explore Anchor Sprint service delivery areas: software development, system integration, digital transformation, cloud architecture, AI & smart manufacturing.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Service Details", url: "/service-details/" },
      ]),
    ],
  },
  "guides/how-to-use-claude-malaysia": {
    schemas: [
      getWebPageSchema({
        name: "How to Access & Use Claude in Malaysia (2026 Guide)",
        url: "/guides/how-to-use-claude-malaysia/",
        description:
          "Is Claude available in Malaysia? Yes. A complete guide to signing up, fixing the credit-card payment problem, plans in MYR, using Claude in Bahasa Malaysia, and the three ways businesses access Claude — by a firm Certified in the Claude Partner Network.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "How to use Claude in Malaysia", url: "/guides/how-to-use-claude-malaysia/" },
      ]),
      getHowToSchema({
        name: "How to sign up for Claude in Malaysia",
        description:
          "Create a Claude account in Malaysia and start using it, including how to handle the USD payment.",
        steps: [
          {
            name: "Go to claude.ai and sign up",
            text: "Open claude.ai, click Sign up, choose Malaysia as your country, enter your email or use Google sign-in, and verify your phone number.",
          },
          {
            name: "Start free, then choose a plan",
            text: "Begin on the free plan, then upgrade to Pro (about RM94/month) or a Team/Enterprise plan when you need more usage or central admin.",
          },
          {
            name: "Handle the USD payment",
            text: "If your Malaysian bank card is declined, subscribe through the Apple App Store or Google Play, or pay with a multi-currency card such as Wise or BigPay.",
          },
        ],
      }),
    ],
  },
  "guides/agent-gpa": {
    schemas: [
      getWebPageSchema({
        name: "Agent GPA: How We Score an AI Agent Before It Ships",
        url: "/guides/agent-gpa/",
        description:
          "Anchor Sprint's evaluation method for AI agents: score the manual process first on time per case, error rate, correction cost and escalation quality, then score every version of the agent against the same cases. The agent does not go live until it beats the baseline.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Agent GPA", url: "/guides/agent-gpa/" },
      ]),
    ],
  },
  "guides/claude-cowork": {
    schemas: [
      getWebPageSchema({
        name: "What is Claude Cowork? The Complete Guide + How to Use It",
        url: "/guides/claude-cowork/",
        description:
          "Claude Cowork is Anthropic's agentic AI for knowledge work on the desktop. What it is, the problems it solves, how to use it, Cowork vs Claude Code vs chat, and its honest limitations — a comprehensive guide for Malaysian teams by a firm Certified in the Claude Partner Network.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Claude Cowork", url: "/guides/claude-cowork/" },
      ]),
      getHowToSchema({
        name: "How to use Claude Cowork",
        description:
          "Get started with Claude Cowork, Anthropic's desktop agent for knowledge work, in four steps.",
        steps: [
          {
            name: "Install the Claude desktop app",
            text: "Download Claude Desktop from claude.com/download and sign in. Cowork requires a paid plan (Pro, Max, Team, or Enterprise) and a supported desktop: Mac, Windows, or Linux (beta).",
          },
          {
            name: "Open Cowork and grant folder access",
            text: "Select the Cowork tab and add the specific folders you want Claude to work in via Settings. It only touches folders you grant, inside an isolated virtual machine on your computer.",
          },
          {
            name: "Describe the task and review the plan",
            text: "Tell Claude the outcome you want rather than step-by-step instructions, then review the approach it proposes before it runs — especially on important folders.",
          },
          {
            name: "Let it run and steer as it goes",
            text: "Cowork executes across files and steps while you watch and correct, then you review the finished output.",
          },
        ],
      }),
    ],
  },
  // ChatGPT plans guide (2026-10-08). Bump dateModified whenever the figures are
  // re-checked against OpenAI's pages (docs/research/chatgpt-plans-malaysia.md).
  // Budget 2027 one-page summary (EN/ms/zh). FAQPage ships from the component.
  "guides/malaysia-budget-2027": {
    schemas: [
      {
        ...getWebPageSchema({
          name: "Budget 2027 Malaysia Summary + PDF: Tax Relief, Wages, Aid",
          url: "/guides/malaysia-budget-2027/",
          description:
            "Budget 2027 Malaysia summary with a free PDF guide (tabled 9 October 2026): individual tax relief rising to RM12,000 from YA 2027, the RM2,000 minimum wage from June 2027, STR and SARA, RM1,500 special financial assistance for civil servants, PTPTN changes and the AI and MDEC measures, checked against the official speech on 10 October 2026.",
          dateModified: "2026-10-15",
        }),
        audio: podcastLanguages.map((language) => ({ "@id": getBudgetPodcastAudioUrl(language) })),
      },
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Malaysia Budget 2027", url: "/guides/malaysia-budget-2027/" },
      ]),
    ],
  },
  "guides/chatgpt-intelligent-ui": {
    schemas: [
      getWebPageSchema({
        name: "ChatGPT Intelligent UI: What It Is & Examples",
        url: "/guides/chatgpt-intelligent-ui/",
        description: "A practical guide to ChatGPT Intelligent UI with visual examples, adjustable controls and prompts to try in ChatGPT.",
        dateModified: "2026-10-10",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "ChatGPT Intelligent UI", url: "/guides/chatgpt-intelligent-ui/" },
      ]),
    ],
  },
  "guides/chatgpt-plans-malaysia": {
    schemas: [
      getWebPageSchema({
        name: "ChatGPT Plans Malaysia: Free, Go, Plus, Pro, Business, Enterprise & API",
        url: "/guides/chatgpt-plans-malaysia/",
        description:
          "Every ChatGPT plan compared for Malaysian teams: price in USD and RM, seats, admin, security and data-training defaults, checked against OpenAI's pages on 8 October 2026.",
        dateModified: "2026-10-08",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "ChatGPT Plans Malaysia", url: "/guides/chatgpt-plans-malaysia/" },
      ]),
    ],
  },
  "guides/claude-plans-malaysia": {
    schemas: [
      getWebPageSchema({
        name: "Claude Plans Malaysia: Compare Free, Pro, Team, Enterprise & API",
        url: "/guides/claude-plans-malaysia/",
        description:
          "A side-by-side comparison of every Claude plan for Malaysian teams — pricing, capabilities, and security across Free, Pro, Max, Team, Enterprise, and the API. Compiled from Anthropic's published pricing.",
        // Bump whenever the plan figures are re-checked against Anthropic's
        // published pricing. A pricing page without a modified date gives an
        // answer engine no way to judge whether the numbers are still current,
        // which is the first thing it needs to know before quoting them.
        dateModified: "2026-08-09",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        {
          name: "Claude Plans Malaysia",
          url: "/guides/claude-plans-malaysia/",
        },
      ]),
    ],
  },
  "guides/claude-privacy-compliance-malaysia": {
    schemas: [
      getWebPageSchema({
        name: "Claude Privacy & Compliance: Individual, Team & Enterprise",
        url: "/guides/claude-privacy-compliance-malaysia/",
        description:
          "Can your admin see your Claude chats? Does Anthropic train on your data? A plain-English guide to privacy, data retention, and compliance (SOC 2, ISO, HIPAA) across Claude Individual, Team, and Enterprise for Malaysian teams — compiled from Anthropic's Privacy Center and Trust Center.",
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        {
          name: "Claude Privacy & Compliance",
          url: "/guides/claude-privacy-compliance-malaysia/",
        },
      ]),
    ],
  },
};

// AI Governance Kit template previews (see src/content/ai-governance/kit.ts).
for (const doc of agkPreviewDocs) {
  const url = agkDocHref(doc);
  schemaConfigs[`ai-governance/templates/${doc.slug}`] = {
    schemas: [
      getWebPageSchema({
        name: doc.seoTitle,
        url,
        description: doc.seoDescription,
        // BM documents (borang-pengakuan…, panduan-penggunaan…) are written in Malay.
        ...(doc.lang === "ms" ? { inLanguage: "ms-MY" } : {}),
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "AI Governance", url: "/ai-governance/" },
        { name: "Templates", url: "/ai-governance/templates/" },
        { name: doc.name, url },
      ]),
    ],
  };
}

// Careers role pages (see src/content/careers.ts): one JobPosting per page.
for (const role of careerRoles) {
  const url = careerRolePath(role);
  schemaConfigs[`careers/${role.slug}`] = {
    schemas: [
      getWebPageSchema({
        name: `${role.title} — Careers at Anchor Sprint`,
        url,
        description: role.seo.description,
      }),
      getBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Careers", url: "/careers/" },
        { name: role.title, url },
      ]),
      getJobPostingSchema({
        title: role.title,
        description: getCareerRoleJobDescriptionHtml(role),
        employmentType: role.employmentType,
        datePosted: role.datePosted,
        validThrough: role.validThrough,
        url,
        identifier: role.slug,
      }),
    ],
  };
}

/**
 * Get schema configs for a given route.
 * Returns empty array if no config exists.
 */
export function getSchemaConfigs(route: string): object[] {
  const cleanRoute = route.startsWith("/") ? route.slice(1) : route;
  const config = schemaConfigs[cleanRoute];
  return config ? config.schemas : [];
}
